import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { fetchJSON, putJSON } from 'api/fetch';
import { RootState } from 'initializers/types';
import createCachedSelector from 're-reselect';
import { createSelector } from 'reselect';

// SHARED THUNKS
import {
  sharedCreateNewAvailability,
  sharedDeleteAvailability,
  sharedEditAvailability,
} from 'features/availability/sharedThunks';
// SHARED TYPES
import {
  Availability,
  AvailabilityTemplate,
  CreateAvailabilityPayload,
  Job,
} from 'features/availability/types';

import { toI18n } from 'util/i18n';

import { TEAM_AVAILABILITY_SLICE } from './constants';

// TYPES / INTERFACES

export interface UserAvailability {
  id: number; // User ID
  full_name: string;
  avatar: string;
  next_template_at: string | null; // Date string like "05/03/2025" or null
  canApproveAvailabilities?: boolean; // Optional, if it comes with user data
  availability_templates: AvailabilityTemplate[]; // Renamed for API consistency
  availabilities: Availability[]; // User's current, direct availabilities
  jobs: Job[];
}

interface TeamAvailabilityState {
  users: UserAvailability[];
  dateRange: {
    start_date: string;
    end_date: string;
  };
  status: 'idle' | 'pending' | 'resolved' | 'rejected';
  error: string | null | unknown;
  availabilityError: string | null;
}

const initialState: TeamAvailabilityState = {
  users: [],
  dateRange: {
    start_date: '',
    end_date: '',
  },
  status: 'idle',
  error: null,
  availabilityError: null,
};

// THUNKS
export const fetchTeamAvailabilities = createAsyncThunk<
  UserAvailability[],
  { date: string },
  { rejectValue: unknown; state: RootState }
>(
  `${TEAM_AVAILABILITY_SLICE}/fetchTeamAvailabilities`,
  async ({ date }, { rejectWithValue }) => {
    try {
      const response = await fetchJSON(`/users/availabilities?date=${date}`);
      return response;
    } catch (error: unknown) {
      const apiError = error as any;
      const errorPayload = await apiError.response?.json();
      return rejectWithValue(
        errorPayload?.errors?.[0] || toI18n('errors.generic')
      );
    }
  }
);

export const approveRejectTemplate = createAsyncThunk<
  AvailabilityTemplate, // Return type (updated template data, not user)
  {
    templateUuid: string;
    userId: number; // Keep this to know which user to fetch in the second API call
    status: 'approved' | 'rejected';
    managerNotes: string;
  },
  { rejectValue: unknown; state: RootState }
>(
  `${TEAM_AVAILABILITY_SLICE}/approveRejectTemplate`,
  async ({ templateUuid, status, managerNotes }, { rejectWithValue }) => {
    try {
      const payload = {
        availability_template: {
          status,
          manager_notes: managerNotes,
          notify: true,
        },
      };

      // This PUT request returns the updated template
      const response = await putJSON(
        `/availability_templates/${templateUuid}`,
        payload
      );
      return response;
    } catch (error: unknown) {
      const apiError = error as any;
      const errorPayload = await apiError.response?.json();
      return rejectWithValue(
        errorPayload?.errors?.[0] || toI18n('errors.generic')
      );
    }
  }
);

// Fetch updated availabilities after template approval/rejection
export const fetchUserAvailabilities = createAsyncThunk<
  UserAvailability,
  { userId: number; date: string },
  { rejectValue: unknown; state: RootState }
>(
  `${TEAM_AVAILABILITY_SLICE}/fetchUserAvailabilities`,
  async ({ userId, date }, { rejectWithValue }) => {
    try {
      // Call the API to get the updated user data with the latest availabilities
      const response = await fetchJSON(
        `/users/${userId}/availabilities_with_templates?date=${date}`
      );
      return response;
    } catch (error: unknown) {
      const apiError = error as any;
      const errorPayload = await apiError.response?.json();
      return rejectWithValue(
        errorPayload?.errors?.[0] || toI18n('errors.generic')
      );
    }
  }
);

// Parent thunk to handle the complete approval/rejection flow
export const approveRejectAndUpdate = createAsyncThunk<
  UserAvailability,
  {
    templateUuid: string;
    userId: number;
    status: 'approved' | 'rejected';
    managerNotes: string;
    date: string;
  },
  { rejectValue: unknown; state: RootState }
>(
  `${TEAM_AVAILABILITY_SLICE}/approveRejectAndUpdate`,
  async (params, { dispatch, rejectWithValue }) => {
    try {
      // First, approve or reject the template
      await dispatch(
        approveRejectTemplate({
          templateUuid: params.templateUuid,
          userId: params.userId,
          status: params.status,
          managerNotes: params.managerNotes,
        })
      ).unwrap();

      // Then fetch updated user data with latest availabilities
      const response = await dispatch(
        fetchUserAvailabilities({
          userId: params.userId,
          date: params.date,
        })
      ).unwrap();

      return response;
    } catch (error: unknown) {
      // The error is already processed by the child thunks
      return rejectWithValue(error);
    }
  }
);

// SHARED THUNKS (TODO: add extra reducer cases)
export const createNewAvailability = sharedCreateNewAvailability(
  TEAM_AVAILABILITY_SLICE
);
export const editAvailability = sharedEditAvailability(TEAM_AVAILABILITY_SLICE);
export const deleteAvailability = sharedDeleteAvailability(
  TEAM_AVAILABILITY_SLICE
);

/**
 * Enhanced create availability thunk that follows the backbone pattern.
 *
 * The backbone code uses a dual API call approach:
 * 1. POST to create the availability
 * 2. GET to refetch user data with latest availabilities
 *
 * Initially tried handling this with complex logic in the extraReducer for
 * createNewAvailability.fulfilled, but it became too complex with multiple
 * branching paths for templates vs direct availabilities, manual state
 * synchronization, and nested updates.
 *
 * This approach is simpler, more reliable, and matches the backbone behavior.
 */
export const createNewAvailabilityAndRefresh = createAsyncThunk<
  UserAvailability,
  CreateAvailabilityPayload & { userId: number },
  { rejectValue: unknown; state: RootState }
>(
  `${TEAM_AVAILABILITY_SLICE}/createNewAvailabilityAndRefresh`,
  async (params, { dispatch, rejectWithValue }) => {
    try {
      await dispatch(
        createNewAvailability({
          availability: params.availability,
          date: params.date,
        })
      ).unwrap();

      const updatedUser = await dispatch(
        fetchUserAvailabilities({
          userId: params.userId,
          date: params.date!,
        })
      ).unwrap();

      return updatedUser;
    } catch (error: unknown) {
      return rejectWithValue(error);
    }
  }
);

// SLICE
const teamAvailabilitySlice = createSlice({
  name: TEAM_AVAILABILITY_SLICE,
  initialState,
  reducers: {
    setDateRange(state, action) {
      state.dateRange = action.payload;
    },
    resetAvailabilityError(state) {
      state.availabilityError = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchTeamAvailabilities.pending, state => {
        state.status = 'pending';
        state.error = null;
      })
      .addCase(
        fetchTeamAvailabilities.fulfilled,
        (state, action: PayloadAction<UserAvailability[]>) => {
          state.status = 'resolved';
          state.users = action.payload;
        }
      )
      .addCase(fetchTeamAvailabilities.rejected, (state, action) => {
        state.status = 'rejected';
        state.error = action.error.message || toI18n('errors.generic');
      })
      .addCase(approveRejectTemplate.pending, state => {
        // No need to modify status, parent thunk handles it
        state.error = null;
      })
      .addCase(approveRejectTemplate.fulfilled, state => {
        // No need to modify status, parent thunk handles it
        state.error = null;
      })
      .addCase(approveRejectTemplate.rejected, (state, action) => {
        state.status = 'rejected';
        state.error = action.error.message || toI18n('errors.generic');
      })
      .addCase(fetchUserAvailabilities.pending, state => {
        // No need to modify status, parent thunk handles it
        state.error = null;
      })
      .addCase(
        fetchUserAvailabilities.fulfilled,
        (state, action: PayloadAction<UserAvailability>) => {
          // No need to modify status, parent thunk handles it
          const updatedUser = action.payload;
          const userIndex = state.users.findIndex(
            user => user.id === updatedUser.id
          );

          if (userIndex !== -1) {
            // Update the user's availabilities and templates
            state.users[userIndex] = updatedUser;
          }
        }
      )
      .addCase(fetchUserAvailabilities.rejected, (state, action) => {
        state.status = 'rejected';
        state.error = action.error.message || toI18n('errors.generic');
      })
      // Parent thunk
      .addCase(approveRejectAndUpdate.pending, state => {
        // Pending status handled in ApproveOrRejectModal with isSubmitting
        state.error = null;
      })
      .addCase(approveRejectAndUpdate.fulfilled, state => {
        // resolved status handled here in parent thunk
        state.status = 'resolved';
      })
      .addCase(approveRejectAndUpdate.rejected, (state, action) => {
        state.status = 'rejected';
        state.error = action.error.message || toI18n('errors.generic');
      })
      .addCase(editAvailability.pending, state => {
        state.availabilityError = null;
      })
      .addCase(editAvailability.fulfilled, (state, action) => {
        state.status = 'resolved';
        const { id: targetId } = action.payload;

        // collect availabilities and nested template availabilities
        const allAvails = state.users.flatMap(user => [
          ...user.availabilities,
          ...(user.availability_templates?.flatMap(
            template => template.availabilities
          ) ?? []),
        ]);

        allAvails.forEach(avail => {
          if (avail.id === targetId) Object.assign(avail, action.payload);
        });
      })
      .addCase(editAvailability.rejected, (state, action) => {
        state.status = 'rejected';
        state.availabilityError = action.payload || toI18n('errors.generic');
      })
      .addCase(deleteAvailability.pending, state => {
        state.availabilityError = null;
      })
      .addCase(deleteAvailability.fulfilled, (state, action) => {
        state.status = 'resolved';
        const { id: targetId } = action.payload;

        state.users.forEach(user => {
          user.availabilities = user.availabilities.filter(
            availability => availability.id !== targetId
          );
          user.availability_templates?.forEach(template => {
            template.availabilities = template.availabilities.filter(
              availability => availability.id !== targetId
            );
          });
        });
      })
      .addCase(deleteAvailability.rejected, (state, action) => {
        state.status = 'rejected';
        state.availabilityError = action.payload || toI18n('errors.generic');
      })
      .addCase(createNewAvailabilityAndRefresh.pending, state => {
        state.availabilityError = null;
      })
      .addCase(createNewAvailabilityAndRefresh.fulfilled, (state, action) => {
        state.status = 'resolved';
        const updatedUser = action.payload;
        const userIndex = state.users.findIndex(
          user => user.id === updatedUser.id
        );

        if (userIndex !== -1) {
          state.users[userIndex] = updatedUser;
        }
      })
      .addCase(createNewAvailabilityAndRefresh.rejected, (state, action) => {
        state.status = 'rejected';
        state.availabilityError = action.payload || toI18n('errors.generic');
      });
  },
});

// SELECTORS
export const selectTeamAvailabilitySlice = (state: RootState) =>
  state.get(TEAM_AVAILABILITY_SLICE);

export const selectTeamAvailabilities = createSelector(
  [selectTeamAvailabilitySlice],
  (sliceData): UserAvailability[] => sliceData.users
);

export const selectOrderedTeamAvailabilities = createSelector(
  [selectTeamAvailabilities],
  (users: UserAvailability[]) =>
    [...users].sort((a, b) => a.full_name.localeCompare(b.full_name))
);

export const selectTeamAvailabilitiesIsLoading = (
  state: RootState
): boolean => {
  const { status, users } = state.get(TEAM_AVAILABILITY_SLICE);
  // pending → loading
  // idle but no users ever fetched → loading
  return status === 'pending' || (status === 'idle' && users.length === 0);
};

export const selectTeamAvailabilityDateRange = createSelector(
  [selectTeamAvailabilitySlice],
  (sliceData): { start_date: string; end_date: string } => sliceData.dateRange
);

export const selectSortedUserTemplates = (userId: number) =>
  createSelector([selectTeamAvailabilities], (users: UserAvailability[]) => {
    const user = users.find(u => u.id === userId);
    if (!user?.availability_templates?.length) return [];

    return [...user.availability_templates].sort((a, b) => {
      // Put pending templates first
      if (a.status === 'pending' && b.status !== 'pending') return -1;
      if (a.status !== 'pending' && b.status === 'pending') return 1;

      if (a.start_at && b.start_at) {
        return b.start_at.localeCompare(a.start_at);
      }

      return 0;
    });
  });

export const selectUserByAvailabilityId = createCachedSelector(
  [
    selectTeamAvailabilities,
    (_: RootState, availabilityId: number) => availabilityId,
  ],
  (users: UserAvailability[], availabilityId: number) =>
    users.find(user => {
      // Check user's direct availabilities
      if (user.availabilities.some(avail => avail.id === availabilityId)) {
        return true;
      }

      // Check availabilities within templates
      return user.availability_templates?.some(template =>
        template.availabilities.some(avail => avail.id === availabilityId)
      );
    })
)(
  (_: RootState, availabilityId: number) =>
    `selectUserByAvailabilityId-${availabilityId}`
);

export const selectUserById = createCachedSelector(
  [selectTeamAvailabilities, (_: RootState, userId: number) => userId],
  (users: UserAvailability[], userId: number) =>
    users.find(user => user.id === userId)
)((_: RootState, userId: number) => `selectUserById-${userId}`);

// Helper to format jobs into location options (with an "All" entry)
const formatLocationsWithAllOption = (jobs: Job[] = []) => {
  const locationOptions = jobs
    .map(job => ({
      value: job.id, // NOT LOCATION_ID
      label: job.location_name,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  return [
    {
      value: 'all',
      label: toI18n('my_availability_react.availability_modal.all_locations'),
    },
    ...locationOptions,
  ];
};

export const selectUserLocationOptions = createCachedSelector(
  [selectTeamAvailabilities, (_: RootState, userId: number) => userId],
  (users: UserAvailability[], userId: number) => {
    const userJobs = users.find(u => u.id === userId)?.jobs || [];
    return formatLocationsWithAllOption(userJobs);
  }
)((_: RootState, userId: number) => `selectUserLocationOptions-${userId}`);

export const selectAvailabilityError = (state: RootState) =>
  state.get(TEAM_AVAILABILITY_SLICE).availabilityError;

export const { setDateRange, resetAvailabilityError } =
  teamAvailabilitySlice.actions;
export const { reducer, actions } = teamAvailabilitySlice;
